import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import momentTz from "moment-timezone";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { api } from "services/ApiService";
import { ApiEndpoint, Method } from "config/constants/apiConstants";

import patientAvatar from "./avatar.png";

import "./index.css";

/* =========================================================
   CONSTANTS
   ========================================================= */

const MAX_VISIBLE = 5;

const BADGE_CLASS = {
  PENDING: "appointmentStatus-pending",
  COMPLETED: "appointmentStatus-completed",
  CANCELED: "appointmentStatus-canceled",
  ONGOING: "appointmentStatus-ongoing",
};

const CARD_CLASS = {
  PENDING: "appointmentCard-pending",
  COMPLETED: "appointmentCard-completed",
  CANCELED: "appointmentCard-canceled",
  ONGOING: "appointmentCard-ongoing",
};

const normalizeStatus = (s) =>
  String(s || "").toUpperCase().replace("CANCELLED", "CANCELED");

/* =========================================================
   COMPONENT
   ========================================================= */

export const Appointments = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appointments, setAppointments] = useState([]);

  const localTz = useMemo(
    () => localStorage.getItem("Time-Zone") || momentTz.tz.guess() || "UTC",
    [],
  );
  const [userTimezone, setUserTimezone] = useState(localTz);

  /* ---------------- fetch ---------------- */
  const getAppointments = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api(
        ApiEndpoint.GET_APPOINTMENTS,
        Method.GET,
        { timeFilter: "UPCOMING" },
        null,
      );
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setAppointments(list);

      const patientTz = list[0]?.Patient?.User?.timezone;
      if (patientTz) setUserTimezone(patientTz);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError("Unable to load appointments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAppointments();
  }, [getAppointments]);

  /* ---------------- navigation ---------------- */
  const openAppointment = (appointment) => {
    if (!appointment?.appointmentId) return;
    navigate(`/appointment-consultation/${appointment.appointmentId}`, {
      state: { appointment },
    });
  };

  const handleKeyDown = (e, appointment) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAppointment(appointment);
    }
  };

  /* ---------------- upcoming filter ---------------- */
  const upcoming = useMemo(() => {
    const nowUtc = momentTz.utc();

    return appointments
      .filter((a) => {
        if (!a?.slotTime) return false;
        // Parse as UTC — the backend sends a naive ISO string, not local.
        const slot = momentTz.utc(a.slotTime);
        return slot.isValid() && slot.isAfter(nowUtc);
      })
      .sort((a, b) => momentTz.utc(a.slotTime).valueOf() - momentTz.utc(b.slotTime).valueOf())
      .slice(0, MAX_VISIBLE);
  }, [appointments]);

  /* ---------------- render ---------------- */
  return (
    <div className="upcomingAppointments">
      {/* ---------- header ---------- */}
      <div className="upcomingAppointmentsHeader">
        <h4>Upcoming appointments</h4>

        <Link to="/appointments" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<NavigateNextIcon />}
            sx={{ textTransform: "none" }}
          >
            See all
          </Button>
        </Link>
      </div>

      {/* ---------- loading ---------- */}
      {loading && (
        <div className="upcomingAppointmentsState">
          <CircularProgress size={22} />
          <span>Loading appointments…</span>
        </div>
      )}

      {/* ---------- error ---------- */}
      {!loading && error && (
        <div className="upcomingAppointmentsState upcomingAppointmentsState--error">
          <p>{error}</p>
          <Button variant="outlined" size="small" onClick={getAppointments}>
            Retry
          </Button>
        </div>
      )}

      {/* ---------- empty ---------- */}
      {!loading && !error && upcoming.length === 0 && (
        <div className="upcomingAppointmentsState">
          <p>No upcoming appointments.</p>
          <Link to="/doctors" style={{ textDecoration: "none" }}>
            <Button variant="contained" size="small" sx={{ textTransform: "none" }}>
              Book a consultation
            </Button>
          </Link>
        </div>
      )}

      {/* ---------- list ---------- */}
      {!loading && !error && upcoming.length > 0 && (
        <Stack spacing={1.5}>
          {upcoming.map((appointment) => {
            const statusKey = normalizeStatus(appointment.status);
            const cardClass = CARD_CLASS[statusKey] || "";
            const badgeClass = BADGE_CLASS[statusKey] || "";

            // Parse as UTC, display in user's timezone.
            const slot = momentTz
              .utc(appointment.slotTime)
              .tz(userTimezone);

            const doctorName =
              appointment?.Doctor?.name || "Doctor";

            return (
              <Paper
                key={appointment.appointmentId}
                elevation={0}
                className={`appointmentData appointmentDataClickable ${cardClass}`}
                role="button"
                tabIndex={0}
                onClick={() => openAppointment(appointment)}
                onKeyDown={(e) => handleKeyDown(e, appointment)}
                aria-label={`Open appointment with ${doctorName}`}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 1, sm: 2 }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  {/* Doctor */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Avatar
                      alt={doctorName}
                      src={
                        appointment?.Doctor?.User?.profileImagePath ||
                        patientAvatar
                      }
                      className="appointmentDoctorAvatar"
                    />

                    <Box minWidth={0}>
                      <h6 className="appointmentDoctorName">{doctorName}</h6>
                      <p className="appointmentDoctorType">
                        {appointment?.appointmentType || "Consultation"}
                      </p>
                    </Box>
                  </Stack>

                  {/* Date */}
                  <Box sx={{ minWidth: 0 }}>
                    <p className="appointmentFieldLabel">Date</p>
                    <p className="appointmentFieldValue">
                      {slot.format("DD MMM YYYY")}
                    </p>
                  </Box>

                  {/* Time */}
                  <Box sx={{ minWidth: 0 }}>
                    <p className="appointmentFieldLabel">Time</p>
                    <p className="appointmentFieldValue">
                      {slot.format("hh:mm A")}
                    </p>
                  </Box>

                  {/* Status */}
                  <span className={`appointmentStatusBadge ${badgeClass}`}>
                    {appointment.status || "—"}
                  </span>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
};